import { useCallback, useEffect, useMemo, useState, type FormEvent, type ReactElement } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BooksDocumentsGrid, type BookDocumentRow } from '../grids/BooksDocumentsGrid';
import { BooksTiles, type BooksKindFilter } from '../summary/BooksTiles';
import {
  ACCOUNTS_API_URL,
  AFES_API_URL,
  DEFAULT_LIST_PAGE_SIZE,
  JIB_API_URL,
  PURCHASE_INVOICES_API_URL,
  ROYALTY_PAYABLES_API_URL,
  SALES_INVOICES_API_URL,
  SEARCH_API_URL,
  VENDORS_PAGE_URL,
  VOLUME_RUNS_API_URL
} from '../../constants';
import { paginateRows } from '../../domain/contracts';

type AuthFetch = (url: string, init?: RequestInit) => Promise<Response>;

interface InvoiceRow {
  id: string;
  kind: string;
  status: string;
  periodId: string;
  amount: number;
  direction: string;
  partyId?: string;
  volumeRunId?: string;
  accountCode?: string;
  afeNumber?: string;
  pricingStatus?: string;
  missingQuoteDates?: string[];
}

interface VolumeRun {
  id: string;
  periodId: string;
  product: string;
  gross: number;
  unit: string;
}

interface JibRow {
  id: string;
  periodId: string;
  gross: number;
  status: string;
  contractId: string;
  accountCode?: string;
  lines: { partyId: string; workingInterest: string; share: number }[];
}

async function readJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    throw new Error((await res.text()) || `Request failed (${res.status})`);
  }
  return (await res.json()) as T;
}

function defaultAccount(kind: string): string {
  if (kind === 'Royalty') {
    return '2100';
  }
  if (kind === 'Purchase') {
    return '2000';
  }
  if (kind === 'JIB') {
    return '1200';
  }
  return '4100';
}

function missingDays(dates: string[]): string {
  const days = dates.map((day) => day.slice(5));
  if (days.length < 2) {
    return days[0] ?? '';
  }
  if (days.length === 2) {
    return `${days[0]} and ${days[1]}`;
  }
  return `${days.slice(0, -1).join(', ')}, and ${days[days.length - 1]}`;
}

function issueUrl(row: BookDocumentRow): string {
  switch (row.kind) {
    case 'Sales':
      return `${SALES_INVOICES_API_URL}/${row.id}/issue`;
    case 'Royalty':
      return `${ROYALTY_PAYABLES_API_URL}/${row.id}/issue`;
    case 'Purchase':
      return `${PURCHASE_INVOICES_API_URL}/${row.id}/issue`;
    default:
      return `${JIB_API_URL}/${row.id}/issue`;
  }
}

export function BooksAccountingPanel({
  authFetch,
  canPost,
  periodId,
  productionDate,
  error,
  setError
}: {
  authFetch: AuthFetch;
  canPost: boolean;
  periodId: string;
  productionDate: string;
  error: string;
  setError: (value: string) => void;
}): ReactElement {
  const [docs, setDocs] = useState<BookDocumentRow[]>([]);
  const [runs, setRuns] = useState<VolumeRun[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<BooksKindFilter>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [compose, setCompose] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_LIST_PAGE_SIZE);
  const [volumeRunId, setVolumeRunId] = useState('');
  const [billTo, setBillTo] = useState('');
  const [salesAmount, setSalesAmount] = useState('50000');
  const [royaltyPrice, setRoyaltyPrice] = useState('70');
  const [vendorId, setVendorId] = useState('');
  const [purchaseAmount, setPurchaseAmount] = useState('1000');
  const [wellId, setWellId] = useState('');
  const [joaId, setJoaId] = useState('');
  const [jibGross, setJibGross] = useState('1000');
  const [preview, setPreview] = useState('');
  const [accounts, setAccounts] = useState<{ code: string; name: string; documentKind: string }[]>([]);
  const [afes, setAfes] = useState<{ number: string; name: string; status: string }[]>([]);
  const [issueAccount, setIssueAccount] = useState('4100');
  const [issueAfe, setIssueAfe] = useState('');
  const [searchParams] = useSearchParams();
  const volumeRunFilter = searchParams.get('volumeRun');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, r, p, j, v] = await Promise.all([
        readJson<InvoiceRow[]>(await authFetch(SALES_INVOICES_API_URL)),
        readJson<InvoiceRow[]>(await authFetch(ROYALTY_PAYABLES_API_URL)),
        readJson<InvoiceRow[]>(await authFetch(PURCHASE_INVOICES_API_URL)),
        readJson<JibRow[]>(await authFetch(JIB_API_URL)),
        readJson<VolumeRun[]>(await authFetch(VOLUME_RUNS_API_URL))
      ]);
      const next: BookDocumentRow[] = [
        ...s.map((row) => ({ ...row, kind: 'Sales' })),
        ...r.map((row) => ({ ...row, kind: 'Royalty' })),
        ...p.map((row) => ({ ...row, kind: 'Purchase' })),
        ...j.map((row) => ({
          id: row.id,
          kind: 'JIB',
          status: row.status,
          periodId: row.periodId,
          amount: row.gross,
          direction: 'AR',
          accountCode: row.accountCode,
          lines: row.lines
        }))
      ];
      setDocs(next);
      setRuns(v);
      setVolumeRunId((current) => current || v[0]?.id || '');
      try {
        setAccounts(await readJson(await authFetch(ACCOUNTS_API_URL)));
        setAfes(await readJson(await authFetch(AFES_API_URL)));
      } catch {
        setAccounts([]);
        setAfes([]);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Books load failed');
    } finally {
      setLoading(false);
    }
  }, [authFetch, setError]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void (async () => {
      const data = await readJson<{
        parties: { id: string; label: string }[];
        contracts: { id: string; label: string }[];
        wells: { id: string; label: string }[];
      }>(await authFetch(`${SEARCH_API_URL}?q=${encodeURIComponent('T-12')}`));
      const t12 = data.contracts.find((c) => c.label.includes('T-12') || c.label.includes('JOA'));
      if (t12) {
        setJoaId(t12.id);
      }
      const well = data.wells[0];
      if (well) {
        setWellId(well.id);
      }
      const parties = await readJson<{
        parties: { id: string; label: string }[];
      }>(await authFetch(`${SEARCH_API_URL}?q=${encodeURIComponent('Citation')}`));
      const ccm = parties.parties.find((p) => p.label.includes('Crude')) ?? parties.parties[0];
      if (ccm) {
        setBillTo(ccm.id);
      }
      try {
        const vendors = await readJson<{ items: { id: string; name?: string; legalName?: string }[] }>(
          await authFetch(`${VENDORS_PAGE_URL}?page=1&pageSize=20`)
        );
        if (vendors.items[0]) {
          setVendorId(vendors.items[0].id);
        }
      } catch {
        const partner = parties.parties.find((p) => p.label.includes('Partner'));
        if (partner) {
          setVendorId(partner.id);
        }
      }
    })().catch(() => undefined);
  }, [authFetch]);

  const filtered = useMemo(() => {
    let rows = docs;
    if (volumeRunFilter) {
      rows = rows.filter(
        (d) => d.volumeRunId === volumeRunFilter && (d.kind === 'Sales' || d.kind === 'Royalty')
      );
    }
    if (filter === 'all') {
      return rows;
    }
    const kind = filter === 'jib' ? 'JIB' : filter === 'sales' ? 'Sales' : filter === 'royalty' ? 'Royalty' : 'Purchase';
    return rows.filter((d) => d.kind === kind);
  }, [docs, filter, volumeRunFilter]);

  const counts = useMemo(
    () => ({
      all: docs.length,
      sales: docs.filter((d) => d.kind === 'Sales').length,
      royalty: docs.filter((d) => d.kind === 'Royalty').length,
      purchase: docs.filter((d) => d.kind === 'Purchase').length,
      jib: docs.filter((d) => d.kind === 'JIB').length
    }),
    [docs]
  );

  const pageRows = useMemo(() => paginateRows(filtered, page, pageSize), [filtered, page, pageSize]);
  const selected = docs.find((d) => d.id === selectedId) ?? null;

  const post = async (url: string, body: unknown) => {
    setError('');
    await readJson(await authFetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }));
    setCompose(false);
    await load();
  };

  const onSales = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await post(SALES_INVOICES_API_URL, { volumeRunId, billToPartyId: billTo, amount: Number(salesAmount) });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sales invoice failed');
    }
  };

  const onRoyalty = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await post(ROYALTY_PAYABLES_API_URL, { volumeRunId, pricePerUnit: Number(royaltyPrice) });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Royalty failed');
    }
  };

  const onPurchase = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await post(PURCHASE_INVOICES_API_URL, {
        periodId,
        vendorPartyId: vendorId,
        amount: Number(purchaseAmount),
        wellId: wellId || undefined
      });
      if (joaId) {
        const rows = await readJson<{ partyId: string; workingInterest: string; share: number }[]>(
          await authFetch(
            `${PURCHASE_INVOICES_API_URL}/wi-preview?contractId=${encodeURIComponent(joaId)}&amount=${encodeURIComponent(purchaseAmount)}&productionDate=${encodeURIComponent(productionDate)}`
          )
        );
        setPreview(rows.map((r) => `${r.workingInterest} → ${r.share}`).join('; '));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Purchase invoice failed');
    }
  };

  const onJib = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await post(JIB_API_URL, { periodId, contractId: joaId, grossAmount: Number(jibGross) });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'JIB failed');
    }
  };

  const issue = async (row: BookDocumentRow) => {
    setError('');
    try {
      await readJson(
        await authFetch(issueUrl(row), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            accountCode: issueAccount || defaultAccount(row.kind),
            afeNumber: row.kind === 'Purchase' ? issueAfe || undefined : undefined
          })
        })
      );
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Issue failed');
    }
  };

  return (
    <section data-testid="accounting-books">
      <h3>Books — sales, royalty, well cost, JIB</h3>
      <p>Sales AR and royalty AP are separate documents. Simple JIB is WI × cost; COPAS rates are not applied.</p>
      {volumeRunFilter && (
        <p className="hint" data-testid="books-volume-run-filter">
          Showing sales and royalty for this volume run.
        </p>
      )}
      {error && <p className="error">{error}</p>}
      <BooksTiles
        counts={counts}
        filter={filter}
        onFilter={(next) => {
          setFilter(next);
          setPage(1);
        }}
      />
      <div className={`requests-shell${selectedId || compose ? ' has-panel' : ''}`}>
        <BooksDocumentsGrid
          rows={pageRows}
          selectedId={selectedId}
          onSelect={(id) => {
            setCompose(false);
            setSelectedId(id);
            const row = docs.find((d) => d.id === id);
            if (row) {
              setIssueAccount(row.accountCode || defaultAccount(row.kind));
              setIssueAfe(row.afeNumber || '');
            }
          }}
          onNew={
            canPost
              ? () => {
                  setSelectedId(null);
                  setCompose(true);
                }
              : undefined
          }
          loading={loading}
          page={page}
          pageSize={pageSize}
          totalCount={filtered.length}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
        />
        {compose && canPost && (
          <aside className="side-panel" aria-label="New books document">
            <div className="side-panel-header">
              <h3>New document</h3>
              <button type="button" className="secondary" onClick={() => setCompose(false)} aria-label="Close">
                ×
              </button>
            </div>
            <div className="side-panel-body">
              <form className="grid" onSubmit={onSales} data-testid="sales-invoice-form">
                <label>
                  Volume run
                  <select value={volumeRunId} onChange={(e) => setVolumeRunId(e.target.value)}>
                    {runs.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.periodId} {r.gross} {r.unit}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Sales amount
                  <input value={salesAmount} onChange={(e) => setSalesAmount(e.target.value)} />
                </label>
                <button type="submit">Draft sales invoice</button>
              </form>
              <form className="grid" onSubmit={onRoyalty} data-testid="royalty-form">
                <label>
                  Price per unit
                  <input value={royaltyPrice} onChange={(e) => setRoyaltyPrice(e.target.value)} />
                </label>
                <button type="submit">Draft royalty AP</button>
              </form>
              <form className="grid" onSubmit={onPurchase} data-testid="purchase-invoice-form">
                <label>
                  LOE amount
                  <input value={purchaseAmount} onChange={(e) => setPurchaseAmount(e.target.value)} />
                </label>
                <button type="submit">Draft purchase invoice</button>
              </form>
              {preview && <p data-testid="wi-preview">WI preview: {preview}</p>}
              <form className="grid" onSubmit={onJib} data-testid="jib-form">
                <label>
                  Joint cost
                  <input value={jibGross} onChange={(e) => setJibGross(e.target.value)} />
                </label>
                <button type="submit">Draft simple JIB</button>
              </form>
            </div>
          </aside>
        )}
        {selected && (
          <aside className="side-panel" aria-label="Books document">
            <div className="side-panel-header">
              <h3>
                {selected.kind} {selected.periodId}
              </h3>
              <button type="button" className="secondary" onClick={() => setSelectedId(null)} aria-label="Close">
                ×
              </button>
            </div>
            <div className="side-panel-body">
              <p>
                <span className="status-pill">{selected.status}</span> {selected.direction} {selected.amount}
                {selected.accountCode ? ` · ${selected.accountCode}` : ''}
                {selected.afeNumber ? ` · ${selected.afeNumber}` : ''}
              </p>
              {(selected.kind === 'Sales' || selected.kind === 'Royalty') && (
                <p data-testid="books-pricing">
                  <span className="status-pill">{selected.pricingStatus ?? 'Unpriced'}</span>
                  {selected.missingQuoteDates && selected.missingQuoteDates.length > 0 && (
                    <>
                      {' '}
                      Missing {missingDays(selected.missingQuoteDates)}. As prices arrive
                      the totals are computed.
                    </>
                  )}
                </p>
              )}
              {canPost && selected.status === 'Draft' && (
                <>
                  <label>
                    Account
                    <select
                      data-testid="issue-account"
                      value={issueAccount}
                      onChange={(e) => setIssueAccount(e.target.value)}
                    >
                      {accounts.map((a) => (
                        <option key={a.code} value={a.code}>
                          {a.code} {a.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  {selected.kind === 'Purchase' && (
                    <label>
                      AFE (required when the account says Yes)
                      <select value={issueAfe} onChange={(e) => setIssueAfe(e.target.value)}>
                        <option value="">None</option>
                        {afes
                          .filter((a) => a.status === 'Approved')
                          .map((a) => (
                            <option key={a.number} value={a.number}>
                              {a.number} {a.name}
                            </option>
                          ))}
                      </select>
                    </label>
                  )}
                </>
              )}
              {selected.lines && selected.lines.length > 0 && (
                <p className="hint">
                  Partner lines: {selected.lines.map((l) => `${l.workingInterest} → ${l.share}`).join('; ')}
                </p>
              )}
              {canPost && selected.status === 'Draft' && (
                <button type="button" onClick={() => void issue(selected)}>
                  Issue
                </button>
              )}
            </div>
          </aside>
        )}
      </div>
    </section>
  );
}
